import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmails } from '@/presentation/hooks/useEmails';
import { useTenants } from '@/presentation/hooks/useTenants';
import { Button, ModalShell } from '@/presentation/components/common';
import type {
  EmailMessageType,
  EmailOutboundDTO,
  EmailOutboundStatus,
} from '@/domain/repositories/IEmailOutboundRepository';

const STATUS_LABELS: Record<EmailOutboundStatus, string> = {
  pending: 'Pendiente',
  sent: 'Enviado',
  delivered: 'Entregado',
  opened: 'Abierto',
  clicked: 'Click',
  bounced: 'Rebotado',
  failed: 'Fallido',
  dropped: 'Descartado',
};

const STATUS_COLORS: Record<EmailOutboundStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  sent: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  opened: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  clicked: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  bounced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  dropped: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const TYPE_LABELS: Record<EmailMessageType, string> = {
  reminder: 'Recordatorio',
  marketing: 'Marketing',
  manual: 'Manual',
  panel_collections: 'Cobranza panel',
};

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-AR');
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-gray-500 dark:text-gray-400 text-xs">{label}</span>
      <p className="text-sm text-gray-900 dark:text-white break-all mt-0.5">{value ?? '—'}</p>
    </div>
  );
}

interface EmailDetailModalProps {
  email: EmailOutboundDTO;
  onClose: () => void;
}

const EmailDetailModal: React.FC<EmailDetailModalProps> = ({ email, onClose }) => {
  const { t } = useTranslation();

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      size="lg"
      title={email.subject}
      subtitle={email.recipientEmail}
      footer={
        <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
          {t('pages.emails.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              STATUS_COLORS[email.status] ?? 'bg-gray-100 text-gray-800'
            }`}
          >
            {STATUS_LABELS[email.status] ?? email.status}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200">
            {TYPE_LABELS[email.messageType] ?? email.messageType}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailRow
            label={t('pages.emails.fields.clinic')}
            value={email.tenantName ?? email.tenantId}
          />
          <DetailRow label={t('pages.emails.fields.tenantId')} value={email.tenantId} />
          <DetailRow label={t('pages.emails.fields.recipient')} value={email.recipientEmail} />
          <DetailRow label={t('pages.emails.fields.subject')} value={email.subject} />
          <DetailRow
            label={t('pages.emails.fields.sendgridId')}
            value={email.sendgridMessageId ?? '—'}
          />
          <DetailRow
            label={t('pages.emails.fields.correlationToken')}
            value={email.correlationToken ?? '—'}
          />
          <DetailRow label={t('pages.emails.fields.sentAt')} value={formatDateTime(email.sentAt)} />
          <DetailRow
            label={t('pages.emails.fields.createdAt')}
            value={formatDateTime(email.createdAt)}
          />
          <DetailRow
            label={t('pages.emails.fields.updatedAt')}
            value={formatDateTime(email.updatedAt)}
          />
          <DetailRow
            label={t('pages.emails.fields.turnoId')}
            value={email.turnoId != null ? String(email.turnoId) : '—'}
          />
          <DetailRow
            label={t('pages.emails.fields.campaignId')}
            value={email.campaignId ?? '—'}
          />
          <DetailRow
            label={t('pages.emails.fields.pacienteId')}
            value={email.pacienteId != null ? String(email.pacienteId) : '—'}
          />
        </div>

        {email.errorMessage && (
          <div>
            <span className="text-gray-500 dark:text-gray-400 text-xs">
              {t('pages.emails.fields.error')}
            </span>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded p-3 whitespace-pre-wrap">
              {email.errorMessage}
            </p>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export const EmailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { tenants } = useTenants();

  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState<EmailOutboundStatus | ''>('');
  const [messageType, setMessageType] = useState<EmailMessageType | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<EmailOutboundDTO | null>(null);

  const pageSize = 50;

  const queryParams = useMemo(
    () => ({
      tenantId: tenantId || undefined,
      status: status || undefined,
      messageType: messageType || undefined,
      search: search || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      page,
      pageSize,
    }),
    [tenantId, status, messageType, search, dateFrom, dateTo, page]
  );

  const { emails, total, isLoading, error, refetch } = useEmails(queryParams);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setTenantId('');
    setStatus('');
    setMessageType('');
    setSearchInput('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const sortedTenants = useMemo(
    () => [...tenants].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    [tenants]
  );

  const selectedTenant = sortedTenants.find((tenant) => String(tenant.id) === tenantId);
  const activeFilterChips = [
    ...(tenantId
      ? [{
          key: 'tenant',
          label: `${t('pages.emails.columns.clinic')}: ${selectedTenant?.nombre ?? tenantId}`,
          onRemove: () => {
            setTenantId('');
            setPage(1);
          },
        }]
      : []),
    ...(status
      ? [{
          key: 'status',
          label: `${t('pages.emails.columns.status')}: ${STATUS_LABELS[status] ?? status}`,
          onRemove: () => {
            setStatus('');
            setPage(1);
          },
        }]
      : []),
    ...(messageType
      ? [{
          key: 'type',
          label: `${t('pages.emails.columns.type')}: ${TYPE_LABELS[messageType] ?? messageType}`,
          onRemove: () => {
            setMessageType('');
            setPage(1);
          },
        }]
      : []),
    ...(dateFrom
      ? [{
          key: 'dateFrom',
          label: `${t('pages.emails.dateFrom')}: ${dateFrom}`,
          onRemove: () => {
            setDateFrom('');
            setPage(1);
          },
        }]
      : []),
    ...(dateTo
      ? [{
          key: 'dateTo',
          label: `${t('pages.emails.dateTo')}: ${dateTo}`,
          onRemove: () => {
            setDateTo('');
            setPage(1);
          },
        }]
      : []),
    ...(search
      ? [{
          key: 'search',
          label: `${t('pages.emails.search')}: ${search}`,
          onRemove: () => {
            setSearch('');
            setSearchInput('');
            setPage(1);
          },
        }]
      : []),
  ];
  const hasActiveFilters = activeFilterChips.length > 0 || searchInput.trim().length > 0;

  return (
    <div className="panel-page h-full min-w-0 flex flex-col !pb-4 overflow-hidden">
      <div className="panel-page-header flex-shrink-0">
        <div className="min-w-0">
          <h1 className="panel-page-title">
            {t('pages.emails.title')}
          </h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.emails.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={refetch}
          disabled={isLoading}
          className="btn btn-secondary w-full sm:w-auto"
        >
          {isLoading ? t('pages.emails.refreshing') : t('pages.emails.refresh')}
        </button>
      </div>

      <div className="panel-card p-2 flex-shrink-0">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[minmax(10rem,1fr)_minmax(9rem,0.8fr)_minmax(9rem,0.8fr)_minmax(8.5rem,0.7fr)_minmax(8.5rem,0.7fr)_minmax(16rem,1.4fr)_auto_auto]">
          <select
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setPage(1);
            }}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            aria-label={t('pages.emails.allClinics')}
            title={t('pages.emails.allClinics')}
          >
            <option value="">{t('pages.emails.allClinics')}</option>
            {sortedTenants.map((tenant) => (
              <option key={String(tenant.id)} value={String(tenant.id)}>
                {tenant.nombre}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as EmailOutboundStatus | '');
              setPage(1);
            }}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            aria-label={t('pages.emails.allStatuses')}
            title={t('pages.emails.allStatuses')}
          >
            <option value="">{t('pages.emails.allStatuses')}</option>
            {(Object.keys(STATUS_LABELS) as EmailOutboundStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>

          <select
            value={messageType}
            onChange={(e) => {
              setMessageType(e.target.value as EmailMessageType | '');
              setPage(1);
            }}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            aria-label={t('pages.emails.allTypes')}
            title={t('pages.emails.allTypes')}
          >
            <option value="">{t('pages.emails.allTypes')}</option>
            {(Object.keys(TYPE_LABELS) as EmailMessageType[]).map((type) => (
              <option key={type} value={type}>
                {TYPE_LABELS[type]}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            aria-label={t('pages.emails.dateFrom')}
            title={t('pages.emails.dateFrom')}
          />

          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            aria-label={t('pages.emails.dateTo')}
            title={t('pages.emails.dateTo')}
          />

          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch();
            }}
            placeholder={t('pages.emails.searchPlaceholder')}
            className="h-8 min-w-0 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            aria-label={t('pages.emails.search')}
            title={t('pages.emails.search')}
          />
          <button
            type="button"
            onClick={applySearch}
            className="inline-flex h-8 items-center justify-center rounded-md bg-teal-600 px-3 text-xs font-medium leading-none text-white transition-colors hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('pages.emails.search')}
          </button>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white xl:w-8"
            title={t('pages.emails.clearFilters')}
            aria-label={t('pages.emails.clearFilters')}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10" />
            </svg>
            <span className="ml-2 text-sm xl:hidden">{t('pages.emails.clearFilters')}</span>
          </button>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                title={chip.label}
              >
                <span className="truncate">{chip.label}</span>
                <span className="text-sm leading-none" aria-hidden>×</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="flex-shrink-0 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300">
          {error.message}
        </div>
      )}

      <div className="flex-1 min-h-0 min-w-0 panel-table-wrap">
        <p className="panel-table-scroll-hint">Deslizá horizontalmente para ver más columnas</p>
        {isLoading ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {t('pages.emails.loading')}
          </div>
        ) : emails.length === 0 ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {t('pages.emails.noResults')}
          </div>
        ) : (
          <table className="w-full divide-y divide-slate-200 dark:divide-gray-700">
            <thead className="bg-slate-50 dark:bg-gray-900 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-gray-900">
                  {t('pages.emails.columns.clinic')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-gray-900">
                  {t('pages.emails.columns.recipient')}
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell bg-slate-50 dark:bg-gray-900">
                  {t('pages.emails.columns.subject')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden sm:table-cell bg-slate-50 dark:bg-gray-900">
                  {t('pages.emails.columns.type')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-gray-900">
                  {t('pages.emails.columns.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell bg-slate-50 dark:bg-gray-900">
                  {t('pages.emails.columns.date')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-gray-900">
                  {t('pages.emails.columns.action')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-slate-200 dark:divide-gray-700">
              {emails.map((email) => (
                <tr
                  key={email.id}
                  className="hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-900 dark:text-white">
                      {email.tenantName ?? '—'}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 font-mono truncate max-w-40">
                      {email.tenantId}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-900 dark:text-white truncate max-w-48">
                      {email.recipientEmail}
                    </p>
                  </td>
                  <td className="px-4 py-3.5 hidden md:table-cell">
                    <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-1 max-w-56">
                      {email.subject}
                    </p>
                    {email.sendgridMessageId && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 font-mono truncate max-w-56 mt-0.5">
                        {email.sendgridMessageId}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {TYPE_LABELS[email.messageType] ?? email.messageType}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        STATUS_COLORS[email.status] ?? 'bg-slate-100 text-slate-800'
                      }`}
                    >
                      {STATUS_LABELS[email.status] ?? email.status}
                    </span>
                    {email.errorMessage && (
                      <p className="text-xs text-red-500 mt-1 line-clamp-1 max-w-36">
                        {email.errorMessage}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(email.sentAt ?? email.createdAt).toLocaleDateString('es-AR')}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      {new Date(email.sentAt ?? email.createdAt).toLocaleTimeString('es-AR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setSelected(email)}
                      className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 font-medium transition-colors"
                    >
                      {t('pages.emails.view')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && total > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('pages.emails.showing', {
              from: (page - 1) * pageSize + 1,
              to: Math.min(page * pageSize, total),
              total,
            })}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-gray-600 disabled:opacity-40 text-slate-700 dark:text-slate-300"
            >
              {t('pages.emails.prev')}
            </button>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-gray-600 disabled:opacity-40 text-slate-700 dark:text-slate-300"
            >
              {t('pages.emails.next')}
            </button>
          </div>
        </div>
      )}

      {selected && <EmailDetailModal email={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};
